<script setup>
import { computed, ref, unref } from 'vue'
import { getIcon } from '@/tiptap/constants/icons.js'

import { useLocale } from '@/tiptap/locales/index.js'
import CreateTablePopover from './CreateTablePopover.vue'

/**
 * @typedef {Object} TableMenuProps
 * @property {import('@tiptap/vue-3').Editor} editor
 * @property {string=} activator
 */
const props = defineProps(/** @type {TableMenuProps} */({
  editor: undefined,
  activator: undefined,
}))

// Local fallback if needed in future (template currently uses activator="parent")
const activator = props.activator ?? 'parent'

const { t } = useLocale()

const menu = ref(false)
const items = computed(() => {
  return [
    {
      type: 'item',
      key: 'insert-table',
      title: unref(t)('editor.table.menu.insert_table'),
      icon: getIcon('tablePlus'),
      disabled: !props.editor.can().insertTable() || false,
    },
    { type: 'divider' },
    {
      type: 'item',
      key: 'add-column-before',
      title: unref(t)('editor.table.menu.add_column_before'),
      icon: getIcon('tableColumnPlusBefore'),
      disabled: !props.editor.can().addColumnBefore() || false,
    },
    {
      type: 'item',
      key: 'add-column-after',
      title: unref(t)('editor.table.menu.add_column_after'),
      icon: getIcon('tableColumnPlusAfter'),
      disabled: !props.editor.can().addColumnAfter() || false,
    },
    {
      type: 'item',
      key: 'delete-column',
      title: unref(t)('editor.table.menu.delete_column'),
      icon: getIcon('tableColumnRemove'),
      disabled: !props.editor.can().deleteColumn() || false,
    },
    { type: 'divider' },
    {
      type: 'item',
      key: 'add-row-before',
      title: unref(t)('editor.table.menu.add_row_before'),
      icon: getIcon('tableRowPlusBefore'),
      disabled: !props.editor.can().addRowBefore() || false,
    },
    {
      type: 'item',
      key: 'add-row-after',
      title: unref(t)('editor.table.menu.add_row_after'),
      icon: getIcon('tableRowPlusAfter'),
      disabled: !props.editor.can().addRowAfter() || false,
    },
    {
      type: 'item',
      key: 'delete-row',
      title: unref(t)('editor.table.menu.delete_row'),
      icon: getIcon('tableRowRemove'),
      disabled: !props.editor.can().deleteRow() || false,
    },
    { type: 'divider' },
    {
      type: 'item',
      key: 'merge-or-split-cells',
      title: unref(t)('editor.table.menu.merge_or_split_cells'),
      icon: getIcon('tableMergeCells'),
      disabled: !props.editor.can().mergeOrSplit() || false,
    },
    { type: 'divider' },
    {
      type: 'item',
      key: 'delete-table',
      title: unref(t)('editor.table.menu.delete_table'),
      icon: getIcon('tableRemove'),
      disabled: !props.editor.can().deleteTable() || false,
    },
  ]
})

/**
 * @param {('insert-table'|'add-column-before'|'add-column-after'|'delete-column'|'add-row-before'|'add-row-after'|'delete-row'|'merge-or-split-cells'|'delete-table')=} key
 * @param {Object=} options
 */
function setTable(key, options) {
  if (!key) return

  const actions = {
    'insert-table': () => props.editor.chain().focus().insertTable({ ...(options || {}) }).run(),
    'add-column-before': () => props.editor.chain().focus().addColumnBefore().run(),
    'add-column-after': () => props.editor.chain().focus().addColumnAfter().run(),
    'delete-column': () => props.editor.chain().focus().deleteColumn().run(),
    'add-row-before': () => props.editor.chain().focus().addRowBefore().run(),
    'add-row-after': () => props.editor.chain().focus().addRowAfter().run(),
    'delete-row': () => props.editor.chain().focus().deleteRow().run(),
    'merge-or-split-cells': () => props.editor.chain().focus().mergeOrSplit().run(),
    'delete-table': () => props.editor.chain().focus().deleteTable().run(),
  }

  actions[key]?.()
}

/** @param {{rows:number, cols:number, withHeaderRow:boolean}} options */
function createTable(options) {
  setTable('insert-table', options)
  menu.value = false
}
</script>

<template>
  <VMenu v-model="menu" :activator="activator">
    <VList density="compact">
      <template v-for="(item, index) in items">
        <VListItem v-if="item.key === 'insert-table'" :key="index" :disabled="item.disabled">
          <template #prepend>
            <VIcon :icon="item.icon" />
          </template>

          <VListItemTitle>{{ item.title }}</VListItemTitle>

          <CreateTablePopover v-if="item.key === 'insert-table'" :key="index" @create-table="createTable" />
        </VListItem>

        <VListItem
          v-else-if="item.type === 'item'"
          :key="'item-' + index"
          :disabled="item.disabled"
          @click="setTable(item.key)"
        >
          <template #prepend>
            <VIcon :icon="item.icon" />
          </template>

          <VListItemTitle>{{ item.title }}</VListItemTitle>
        </VListItem>

        <VDivider v-else :key="'divider-' + index" />
      </template>
    </VList>
  </VMenu>
</template>